import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LinkIcon from "@mui/icons-material/Link";
import { eliminarMovimientoExtracto, fmtFecha, getMovimientoEliminable } from "../services/egresosService";

const ESTADOS = { Correcto: "Correcto", "No existe": "Pendiente", "Enlazado con Excel": "Enlazado con Extracto", Unico: "Único" };

const Dato = ({ etiqueta, valor }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{etiqueta}</Typography>
    <Typography sx={{ fontSize: 13.5, fontWeight: 700 }} noWrap>{valor || "—"}</Typography>
  </Box>
);

// Eliminar un movimiento del extracto: solo se puede si no tiene ninguna relación (pagos de facturas, cheques, recibos, traspasos o enlaces con
// otros movimientos). Antes de pedir confirmación se consulta al servidor, que además lo vuelve a validar al eliminar.
export default function EliminarMovimientoDialog({ movimiento, onClose, onEliminado }) {
  const [info, setInfo] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!movimiento) return undefined;
    let activo = true;
    setInfo(null);
    setError("");
    setCargando(true);
    getMovimientoEliminable(movimiento.id)
      .then(({ data }) => { if (activo) setInfo(data); })
      .catch((e) => { if (activo) setError(e?.response?.data?.error || "No se pudo revisar el movimiento."); })
      .finally(() => { if (activo) setCargando(false); });
    return () => { activo = false; };
  }, [movimiento]);

  const eliminar = async () => {
    setEliminando(true);
    setError("");
    try {
      await eliminarMovimientoExtracto(movimiento.id);
      onEliminado?.(movimiento);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo eliminar el movimiento.");
    } finally {
      setEliminando(false);
    }
  };

  const monto = movimiento?.debito ? `Débito ${movimiento.debito}` : movimiento?.credito ? `Crédito ${movimiento.credito}` : "";

  return (
    <Dialog open={!!movimiento} onClose={eliminando ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 17, fontWeight: 800 }}>Eliminar movimiento {movimiento?.id}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
            <Dato etiqueta="Fecha" valor={fmtFecha(movimiento?.fecha)} />
            <Dato etiqueta="Monto" valor={monto} />
            <Dato etiqueta="Código de transacción" valor={movimiento?.cod_transaccion} />
            <Dato etiqueta="Banco" valor={movimiento?.banco} />
            <Dato etiqueta="Estado" valor={ESTADOS[movimiento?.estado] || movimiento?.estado} />
          </Box>

          {cargando && <Box sx={{ py: 1, display: "flex", alignItems: "center", gap: 1 }}><CircularProgress size={18} /><Typography sx={{ fontSize: 13 }}>Revisando si tiene relaciones…</Typography></Box>}
          {error && <Alert severity="error">{error}</Alert>}

          {info && !info.eliminable && (
            <Alert severity="warning" icon={<LinkIcon fontSize="small" />}>
              <b>No se puede eliminar.</b> Este movimiento tiene {info.motivos.join(", ")}. Desvincúlalos primero y luego podrás eliminarlo.
            </Alert>
          )}
          {info?.eliminable && (
            <Alert severity="info">
              No tiene ninguna relación, se puede eliminar. Al confirmar se borra del extracto y <b>no se puede deshacer</b>.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={eliminando} sx={{ textTransform: "none" }}>{info && !info.eliminable ? "Cerrar" : "Cancelar"}</Button>
        {info?.eliminable && (
          <Button
            variant="contained" color="error" onClick={eliminar} disabled={eliminando}
            startIcon={eliminando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />} sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Eliminar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
